import { useState, useMemo, useCallback } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

interface EspecialidadItem {
  especialidad: string
  total: number
  porcentaje: number
}

interface PaisItem {
  nombre: string
  total: number
  porcentaje: number
}

interface BaseMedicosTonal2026Data {
  titulo: string
  subtitulo: string
  total_medicos: number
  total_especialidades: number
  total_paises: number
  especialidades: EspecialidadItem[]
  paises: PaisItem[]
  datos_por_pais: Record<string, Record<string, number>>
  google_sheets_url: string
}

export function BaseMedicosTonal2026Page() {
  const { data, isLoading, error } = useDashReport<BaseMedicosTonal2026Data>('base-medicos-tonal-0126')
  const [filtroPais, setFiltroPais] = useState('todos')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('todas')
  const [paisesOpen, setPaisesOpen] = useState(true)
  const [especialidadesOpen, setEspecialidadesOpen] = useState(false)

  const formatNumber = useCallback((num: number) => {
    return num.toLocaleString('es-EC')
  }, [])

  // Compute available specialties based on selected country
  const especialidadesDisponibles = useMemo(() => {
    if (!data) return []
    if (filtroPais !== 'todos') {
      const paisData = data.datos_por_pais[filtroPais] || {}
      return Object.entries(paisData)
        .map(([esp, total]) => ({ especialidad: esp, total }))
        .sort((a, b) => b.total - a.total)
    }
    return [...data.especialidades].sort((a, b) => b.total - a.total)
  }, [data, filtroPais])

  // Compute available countries based on selected specialty
  const paisesDisponibles = useMemo(() => {
    if (!data) return []
    if (filtroEspecialidad !== 'todas') {
      const result: { nombre: string; total: number }[] = []
      for (const [pais, especialidades] of Object.entries(data.datos_por_pais)) {
        if (especialidades[filtroEspecialidad]) {
          result.push({ nombre: pais, total: especialidades[filtroEspecialidad] })
        }
      }
      return result.sort((a, b) => b.total - a.total)
    }
    return data.paises.map(p => ({ nombre: p.nombre, total: p.total }))
  }, [data, filtroEspecialidad])

  // Compute filtered stats and tables
  const computed = useMemo(() => {
    if (!data) return null

    const paisSeleccionado = filtroPais === 'todos' ? null : filtroPais
    const especialidadSeleccionada = filtroEspecialidad === 'todas' ? null : filtroEspecialidad

    let titulo = 'Resumen General'
    let totalMedicos = 0
    const especialidadesContadas = new Set<string>()
    const paisesContados = new Set<string>()

    if (!paisSeleccionado && !especialidadSeleccionada) {
      totalMedicos = data.total_medicos
      titulo = 'Resumen General'
      data.especialidades.forEach(e => especialidadesContadas.add(e.especialidad))
      data.paises.forEach(p => paisesContados.add(p.nombre))
    } else if (paisSeleccionado && !especialidadSeleccionada) {
      titulo = paisSeleccionado
      const paisData = data.datos_por_pais[paisSeleccionado] || {}
      totalMedicos = Object.values(paisData).reduce((a, b) => a + b, 0)
      Object.keys(paisData).forEach(e => especialidadesContadas.add(e))
      paisesContados.add(paisSeleccionado)
    } else if (!paisSeleccionado && especialidadSeleccionada) {
      titulo = especialidadSeleccionada
      for (const [pais, especialidades] of Object.entries(data.datos_por_pais)) {
        if (especialidades[especialidadSeleccionada]) {
          totalMedicos += especialidades[especialidadSeleccionada]
          paisesContados.add(pais)
        }
      }
      especialidadesContadas.add(especialidadSeleccionada)
    } else if (paisSeleccionado && especialidadSeleccionada) {
      titulo = `${especialidadSeleccionada} en ${paisSeleccionado}`
      const paisData = data.datos_por_pais[paisSeleccionado] || {}
      totalMedicos = paisData[especialidadSeleccionada] || 0
      if (totalMedicos > 0) {
        especialidadesContadas.add(especialidadSeleccionada)
        paisesContados.add(paisSeleccionado)
      }
    }

    // Build specialty table data
    let especialidadesTabla: { especialidad: string; total: number; pais?: string }[] = []
    let totalEspecialidades = 0
    const mostrarColumnaPais = !!paisSeleccionado

    if (paisSeleccionado) {
      const paisData = data.datos_por_pais[paisSeleccionado] || {}
      if (especialidadSeleccionada) {
        const total = paisData[especialidadSeleccionada] || 0
        if (total > 0) {
          especialidadesTabla.push({ especialidad: especialidadSeleccionada, total })
          totalEspecialidades = total
        }
      } else {
        for (const [esp, total] of Object.entries(paisData)) {
          especialidadesTabla.push({ especialidad: esp, total })
          totalEspecialidades += total
        }
        especialidadesTabla.sort((a, b) => b.total - a.total)
      }
    } else if (especialidadSeleccionada) {
      const espData = data.especialidades.find(e => e.especialidad === especialidadSeleccionada)
      if (espData) {
        especialidadesTabla.push({ especialidad: espData.especialidad, total: espData.total })
        totalEspecialidades = espData.total
      }
    } else {
      especialidadesTabla = data.especialidades.map(e => ({ especialidad: e.especialidad, total: e.total }))
      totalEspecialidades = data.total_medicos
    }

    // Build country table data
    let paisesTabla: { nombre: string; total: number }[] = []
    let totalPaises = 0

    if (paisSeleccionado) {
      const paisData = data.datos_por_pais[paisSeleccionado] || {}
      let totalPais = 0
      if (especialidadSeleccionada) {
        totalPais = paisData[especialidadSeleccionada] || 0
      } else {
        totalPais = Object.values(paisData).reduce((a, b) => a + b, 0)
      }
      if (totalPais > 0) {
        paisesTabla.push({ nombre: paisSeleccionado, total: totalPais })
        totalPaises = totalPais
      }
    } else if (especialidadSeleccionada) {
      for (const [pais, especialidades] of Object.entries(data.datos_por_pais)) {
        if (especialidades[especialidadSeleccionada]) {
          const total = especialidades[especialidadSeleccionada]
          totalPaises += total
          paisesTabla.push({ nombre: pais, total })
        }
      }
      paisesTabla.sort((a, b) => b.total - a.total)
    } else {
      paisesTabla = data.paises.map(p => ({ nombre: p.nombre, total: p.total }))
      totalPaises = data.total_medicos
    }

    return {
      titulo,
      totalMedicos,
      totalEspecialidades: especialidadesContadas.size,
      totalPaises: paisesContados.size,
      especialidadesTabla,
      totalEspecialidadesTabla: totalEspecialidades,
      mostrarColumnaPais,
      paisSeleccionado,
      paisesTabla,
      totalPaisesTabla: totalPaises,
    }
  }, [data, filtroPais, filtroEspecialidad])

  const handleFiltroPais = useCallback((pais: string) => {
    setFiltroPais(pais)
    // Reset specialty if not available in new country
    if (pais !== 'todos' && data) {
      const paisData = data.datos_por_pais[pais] || {}
      if (filtroEspecialidad !== 'todas' && !paisData[filtroEspecialidad]) {
        setFiltroEspecialidad('todas')
      }
    }
    if (pais !== 'todos') {
      setPaisesOpen(true)
      setEspecialidadesOpen(true)
    }
  }, [data, filtroEspecialidad])

  const handleFiltroEspecialidad = useCallback((especialidad: string) => {
    setFiltroEspecialidad(especialidad)
    // Reset country if not available for new specialty
    if (especialidad !== 'todas' && data) {
      const paisData = data.datos_por_pais[filtroPais]
      if (filtroPais !== 'todos' && (!paisData || !paisData[especialidad])) {
        setFiltroPais('todos')
      }
    }
    if (especialidad !== 'todas') {
      setEspecialidadesOpen(true)
    }
  }, [data, filtroPais])

  const seleccionarPais = useCallback((pais: string) => {
    if (filtroPais === pais) {
      handleFiltroPais('todos')
    } else {
      handleFiltroPais(pais)
    }
  }, [filtroPais, handleFiltroPais])

  const limpiarFiltros = useCallback(() => {
    setFiltroPais('todos')
    setFiltroEspecialidad('todas')
  }, [])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !computed) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href="https://enterprise.tonalstrategies.com/pricing/" target="_blank" rel="noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {data.google_sheets_url && data.google_sheets_url !== '#' && (
                <a href={data.google_sheets_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v2H6V6zm0 4h8v2H6v-2zm0 4h4v2H6v-2z" />
                  </svg>
                  Ver Datos Fuente
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Filtro de País */}
            <div>
              <label htmlFor="select-pais" className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                País
              </label>
              <select
                id="select-pais"
                value={filtroPais}
                onChange={(e) => handleFiltroPais(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
              >
                <option value="todos">Todos los países</option>
                {paisesDisponibles.map((pais) => (
                  <option key={pais.nombre} value={pais.nombre}>
                    {pais.nombre} ({formatNumber(pais.total)} médicos)
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro de Especialidad */}
            <div>
              <label htmlFor="select-especialidad" className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                Especialidad
              </label>
              <select
                id="select-especialidad"
                value={filtroEspecialidad}
                onChange={(e) => handleFiltroEspecialidad(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="todas">Todas las especialidades</option>
                {especialidadesDisponibles.map((esp) => (
                  <option key={esp.especialidad} value={esp.especialidad}>
                    {esp.especialidad} ({formatNumber(esp.total)} médicos)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Botón para limpiar filtros */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={limpiarFiltros}
              className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>{computed.titulo}</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{formatNumber(computed.totalMedicos)}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-indigo-600">{computed.totalEspecialidades}</p>
              <p className="text-sm text-gray-600 mt-1">Especialidades Activas</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{computed.totalPaises}</p>
              <p className="text-sm text-gray-600 mt-1">Países</p>
            </div>
          </div>
        </div>

        {/* Tabla de Países */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setPaisesOpen(!paisesOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-green-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-base lg:text-lg">Distribución por País</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({computed.paisesTabla.length} país{computed.paisesTabla.length !== 1 ? 'es' : ''})
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${paisesOpen ? 'rotate-180' : ''}`}
                style={!paisesOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {paisesOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">País</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {computed.paisesTabla.map((pais) => {
                    const porcentaje = computed.totalPaisesTabla > 0 ? ((pais.total / computed.totalPaisesTabla) * 100).toFixed(2) : '0'
                    return (
                      <tr
                        key={pais.nombre}
                        className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                        onClick={() => seleccionarPais(pais.nombre)}
                      >
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{pais.nombre}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{formatNumber(pais.total)}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">{porcentaje}%</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${porcentaje}%` }} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{formatNumber(computed.totalPaisesTabla)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-green-600">100%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspecialidadesOpen(!especialidadesOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="text-base lg:text-lg">Análisis por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({computed.especialidadesTabla.length} especialidad{computed.especialidadesTabla.length !== 1 ? 'es' : ''})
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${especialidadesOpen ? 'rotate-180' : ''}`}
                style={!especialidadesOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {especialidadesOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Especialidad</th>
                    {computed.mostrarColumnaPais && (
                      <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">País</th>
                    )}
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Total</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {computed.especialidadesTabla.map((esp) => {
                    const porcentaje = computed.totalEspecialidadesTabla > 0 ? ((esp.total / computed.totalEspecialidadesTabla) * 100).toFixed(2) : '0'
                    return (
                      <tr key={esp.especialidad} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                        onClick={() => {
                          if (filtroEspecialidad === esp.especialidad) {
                            handleFiltroEspecialidad('todas')
                          } else {
                            handleFiltroEspecialidad(esp.especialidad)
                          }
                        }}
                      >
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.especialidad}</td>
                        {computed.mostrarColumnaPais && (
                          <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{computed.paisSeleccionado || '-'}</td>
                        )}
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{formatNumber(esp.total)}</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-indigo-600">{porcentaje}%</td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${porcentaje}%` }} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    {computed.mostrarColumnaPais && (
                      <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                    )}
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{formatNumber(computed.totalEspecialidadesTabla)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-indigo-600">100%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Análisis de Base de Datos</p>
        </div>
      </div>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}
